import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useAuth } from "../../context/AuthContext";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PeopleIcon from "@mui/icons-material/People";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { usersAPI } from "../../services/api";
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  Tab, Tabs, Divider
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

export default function UserManagement() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    password: "Welcome123", role_name: "agent",
    team_name: "General", employee_number: ""
  });
  const [emailError, setEmailError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadUsers();
    usersAPI.getTeams().then(res => setTeams(res.data)).catch(() => {});
  }, []);

  const loadUsers = async () => {
    try {
      const res = await usersAPI.getUsers();
      setUsers(res.data.users);
    } catch { setError("Failed to load users."); }
    finally { setLoading(false); }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEditUser = (u) => {
    setEditUser(u);
    setEditForm({
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      employee_number: u.employee_number || "",
      team_name: u.team?.team_name || "",
      role_name: u.role.role_name,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await usersAPI.updateUser(editUser.user_id, editForm);
      setSuccess(`User ${editForm.first_name} ${editForm.last_name} updated successfully.`);
      setEditOpen(false);
      setEditUser(null);
      loadUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Failed to update user.");
    }
  };

  const handleCreate = async () => {
    if (!form.first_name.trim()) { setError("First name is required."); return; }
    if (!form.last_name.trim()) { setError("Last name is required."); return; }
    if (!form.email.trim()) { setError("Email is required."); return; }
    if (!validateEmail(form.email)) { setError("Please enter a valid email address."); return; }
    if (!form.password.trim()) { setError("Password is required."); return; }
    try {
      await usersAPI.createUser({...form, employee_number: form.employee_number || null});
      setSuccess(`User ${form.first_name} ${form.last_name} created successfully.`);
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", password: "Welcome123", role_name: "agent", team_name: "General", employee_number: "" });
      setEmailError("");
      loadUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) setError(detail.map(e => e.msg).join(", "));
      else if (typeof detail === 'string') setError(detail);
      else setError("Failed to create user.");
    }
  };

  const handleResetPassword = async (userId, name) => {
    if (!window.confirm(`Reset password for ${name}? They will need to use "changeme" to login.`)) return;
    try {
      await usersAPI.resetPassword(userId);
      setSuccess(`Password reset for ${name}.`);
    } catch { setError("Failed to reset password."); }
  };

  const handleDeactivate = async (userId, name) => {
    if (!window.confirm(`Deactivate ${name}?`)) return;
    try {
      await usersAPI.deactivateUser(userId);
      setSuccess(`${name} deactivated.`);
      loadUsers();
    } catch { setError("Failed to deactivate user."); }
  };

  const roleColor = { admin: "error", trainer: "warning", supervisor: "info", agent: "success" };

  const filteredUsers = users.filter(u => {
    const matchSearch = search === "" ||
      u.full_name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.employee_number && u.employee_number.toLowerCase().includes(search.toLowerCase()));
    const matchRole = filterRole === "all" || u.role.role_name === filterRole;
    const matchStatus = filterStatus === "all" || (filterStatus === "active" ? u.is_active : !u.is_active);
    return matchSearch && matchRole && matchStatus;
  });

  const downloadBulkTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { first_name: "John", last_name: "Smith", email: "john.smith@example.com", password: "Welcome123", role: "agent", team: "General", employee_number: "EMP001" },
      { first_name: "Jane", last_name: "Doe", email: "jane.doe@example.com", password: "Welcome123", role: "agent", team: "Sales", employee_number: "EMP002" },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "bulk_user_template.xlsx");
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkLoading(true);
    setBulkResults([]);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      const res = await usersAPI.bulkCreateUsers({ users: rows });
      setBulkResults(res.data.results || []);
      if (res.data.success > 0) { setSuccess(`${res.data.success} user(s) created successfully.`); loadUsers(); }
      if (res.data.failed > 0) setError(`${res.data.failed} user(s) failed — see results below.`);
    } catch { setError("Bulk upload failed."); }
    finally { setBulkLoading(false); e.target.value = ""; }
  };

  const exportBulkResults = () => {
    const rows = bulkResults.map(r => ({ Email: r.email, Status: r.status === "success" ? "Created" : "Failed", Message: r.message }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Results");
    XLSX.writeFile(wb, "bulk_create_results.xlsx");
  };

  const exportCSV = () => {
    const headers = ["Full Name", "Email", "Role", "Team", "Employee #", "Status"];
    const rows = filteredUsers.map(u => [u.full_name, u.email, u.role.role_name, u.team?.team_name || "", u.employee_number || "", u.is_active ? "Active" : "Inactive"]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a"); el.href = url; el.download = "users.csv"; el.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const rows = filteredUsers.map(u => ({ "Full Name": u.full_name, "Email": u.email, "Role": u.role.role_name, "Team": u.team?.team_name || "", "Employee #": u.employee_number || "", "Status": u.is_active ? "Active" : "Inactive" }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "users.xlsx");
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="#1a237e" mb={2}>User Management</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tab icon={<PeopleIcon />} label="User List" iconPosition="start" />
        <Tab icon={<GroupAddIcon />} label="Bulk Create Users" iconPosition="start" />
      </Tabs>

      {/* TAB 1 — User List */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField placeholder="Search by name, email or employee number..."
                value={search} onChange={e => setSearch(e.target.value)}
                size="small" sx={{ minWidth: 280 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Role</InputLabel>
                <Select value={filterRole} label="Role" onChange={e => setFilterRole(e.target.value)}>
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="trainer">Trainer</MenuItem>
                  <MenuItem value="supervisor">Supervisor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                {filteredUsers.length} of {users.length} users
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportCSV} disabled={filteredUsers.length === 0}>Export CSV</Button>
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={filteredUsers.length === 0}>Export Excel</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}
                style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
                New User
              </Button>
            </Box>
          </Box>

          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell><b>Name</b></TableCell>
                    <TableCell><b>Email</b></TableCell>
                    <TableCell><b>Employee #</b></TableCell>
                    <TableCell><b>Role</b></TableCell>
                    <TableCell><b>Team</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell><b>Actions</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} align="center"><CircularProgress /></TableCell></TableRow>
                  ) : filteredUsers.map(u => (
                    <TableRow key={u.user_id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: "#1a237e", fontSize: 12 }}>
                            {u.first_name[0]}{u.last_name[0]}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>{u.full_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.employee_number || "—"}</TableCell>
                      <TableCell><Chip label={u.role.role_name} color={roleColor[u.role.role_name]} size="small" /></TableCell>
                      <TableCell>{u.team?.team_name || "—"}</TableCell>
                      <TableCell><Chip label={u.is_active ? "Active" : "Inactive"} color={u.is_active ? "success" : "default"} size="small" /></TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {user?.role === "admin" && <Button size="small" variant="outlined" onClick={() => handleEditUser(u)}>Edit</Button>}
                          {u.is_active && <>
                            <Button size="small" color="warning" onClick={() => handleResetPassword(u.user_id, u.full_name)}>Reset PW</Button>
                            <Button size="small" color="error" startIcon={<PersonOffIcon />} onClick={() => handleDeactivate(u.user_id, u.full_name)}>Deactivate</Button>
                          </>}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      )}

      {/* TAB 2 — Bulk Create */}
      {tab === 1 && (
        <Box sx={{ maxWidth: 800 }}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} color="#1a237e" mb={1}>Bulk Create Users from Excel</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Download the template, fill in the user details and upload to create multiple users at once.
                Required columns: <b>first_name, last_name, email, password, role, team, employee_number</b>
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadBulkTemplate}>
                  Download Template
                </Button>
                <Button variant="contained" component="label" disabled={bulkLoading}
                  startIcon={bulkLoading ? null : <UploadFileIcon />}
                  style={{ background: "linear-gradient(135deg, #2e7d32, #1b5e20)", color: "#ffffff" }}>
                  {bulkLoading ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Processing...</> : "Upload Excel & Create Users"}
                  <input type="file" hidden accept=".xlsx,.xls" onChange={handleBulkUpload} />
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Valid roles: agent, trainer, supervisor, admin. Team names must match existing teams.
              </Typography>
            </CardContent>
          </Card>

          {bulkResults.length > 0 && (
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Results</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {bulkResults.filter(r => r.status === "success").length} created successfully &nbsp;·&nbsp;
                      {bulkResults.filter(r => r.status !== "success").length} failed
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportBulkResults}>
                      Export Results
                    </Button>
                    <Button size="small" onClick={() => setBulkResults([])}>Clear</Button>
                  </Box>
                </Box>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                        <TableCell><b>Email</b></TableCell>
                        <TableCell><b>Status</b></TableCell>
                        <TableCell><b>Message</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkResults.map((r, i) => (
                        <TableRow key={i} sx={{ bgcolor: r.status === "success" ? "#f1f8e9" : "#fff8f8" }}>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>
                            <Chip label={r.status === "success" ? "Created" : "Failed"} size="small"
                              color={r.status === "success" ? "success" : "error"} />
                          </TableCell>
                          <TableCell>{r.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            <TextField label="First Name" value={editForm.first_name || ""}
              onChange={e => setEditForm({...editForm, first_name: e.target.value})} required />
            <TextField label="Last Name" value={editForm.last_name || ""}
              onChange={e => setEditForm({...editForm, last_name: e.target.value})} required />
            <TextField label="Email" type="email" value={editForm.email || ""}
              onChange={e => setEditForm({...editForm, email: e.target.value})}
              required sx={{ gridColumn: "span 2" }} />
            <TextField label="Employee Number" value={editForm.employee_number || ""}
              onChange={e => setEditForm({...editForm, employee_number: e.target.value})} />
            <FormControl>
              <InputLabel>Team</InputLabel>
              <Select value={editForm.team_name || ""} label="Team"
                onChange={e => setEditForm({...editForm, team_name: e.target.value})}>
                {teams.map(t => <MenuItem key={t.team_id} value={t.team_name}>{t.team_name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl sx={{ gridColumn: "span 2" }}>
              <InputLabel>Role</InputLabel>
              <Select value={editForm.role_name || ""} label="Role"
                onChange={e => setEditForm({...editForm, role_name: e.target.value})}>
                <MenuItem value="agent">Agent</MenuItem>
                <MenuItem value="supervisor">Supervisor</MenuItem>
                <MenuItem value="trainer">Trainer</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}
            style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            <TextField label="First Name" value={form.first_name}
              onChange={e => setForm({...form, first_name: e.target.value})} required />
            <TextField label="Last Name" value={form.last_name}
              onChange={e => setForm({...form, last_name: e.target.value})} required />
            <TextField label="Email" type="email" value={form.email}
              onChange={e => { setForm({...form, email: e.target.value}); setEmailError(!e.target.value || validateEmail(e.target.value) ? "" : "Invalid email format"); }}
              error={!!emailError} helperText={emailError}
              required sx={{ gridColumn: "span 2" }} />
            <TextField label="Password" value={form.password}
              onChange={e => setForm({...form, password: e.target.value})} required />
            <TextField label="Employee Number" value={form.employee_number}
              onChange={e => setForm({...form, employee_number: e.target.value})} />
            <FormControl required>
              <InputLabel>Role</InputLabel>
              <Select value={form.role_name} label="Role" onChange={e => setForm({...form, role_name: e.target.value})}>
                <MenuItem value="agent">Agent</MenuItem>
                {user?.role === "admin" && <MenuItem value="supervisor">Supervisor</MenuItem>}
                {user?.role === "admin" && <MenuItem value="trainer">Trainer</MenuItem>}
                {user?.role === "admin" && <MenuItem value="admin">Admin</MenuItem>}
              </Select>
            </FormControl>
            <FormControl required>
              <InputLabel>Team</InputLabel>
              <Select value={form.team_name} label="Team" onChange={e => setForm({...form, team_name: e.target.value})}>
                {(user?.role === "admin" ? teams : teams.filter(t => t.team_name === user?.team_name)).map(t => <MenuItem key={t.team_id} value={t.team_name}>{t.team_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
            Create User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
